import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { LogIn, Eye, EyeOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

export const Login = () => {
  const { t, i18n } = useTranslation();
  const { login } = useAuth();
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const isRTL = i18n.language === 'ar';

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!username.trim() || !password.trim()) return;
    setLoading(true);
    try {
      await login(username, password);
      navigate('/');
    } catch (err) {
      toast.error(isRTL ? 'اسم المستخدم أو كلمة المرور غير صحيحة' : 'Invalid username or password');
    } finally {
      setLoading(false);
    }
  };

  const toggleLanguage = () => {
    const newLang = i18n.language === 'ar' ? 'en' : 'ar';
    localStorage.setItem('appLanguage', newLang);
    i18n.changeLanguage(newLang).then(() => window.location.reload());
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4" dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Language toggle */}
      <button onClick={toggleLanguage}
        className="fixed top-4 right-4 px-3 py-1.5 text-xs font-medium bg-white rounded-full border border-slate-200 text-slate-600 hover:bg-slate-100 shadow-sm">
        {i18n.language === 'ar' ? 'English' : 'العربية'}
      </button>

      <div className="w-full max-w-sm">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg"
            style={{ backgroundColor: 'var(--theme-primary, #0F172A)' }}>
            <span className="text-2xl font-bold" style={{ color: 'var(--theme-accent, #D4AF37)' }}>A</span>
          </div>
          <h1 className="text-2xl font-bold text-slate-900">AZIZI.CO</h1>
          <p className="text-sm text-slate-500 mt-1">{isRTL ? 'تسجيل الدخول إلى حسابك' : 'Sign in to your account'}</p>
        </div>

        {/* Login Form */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className={`text-sm font-medium text-slate-700 block mb-1.5 ${isRTL ? 'text-right' : ''}`}>
                {isRTL ? 'اسم المستخدم' : 'Username'}
              </label>
              <div>
                <input
                  type="text"
                  data-testid="login-username-input"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder={isRTL ? 'أدخل اسم المستخدم' : 'Enter username'}
                  className={`w-full h-10 rounded-lg border border-slate-200 bg-slate-50 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition-colors px-3 ${isRTL ? 'text-right' : ''}`}
                  autoComplete="username"
                />
              </div>
            </div>
            <div>
              <label className={`text-sm font-medium text-slate-700 block mb-1.5 ${isRTL ? 'text-right' : ''}`}>
                {isRTL ? 'كلمة المرور' : 'Password'}
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  data-testid="login-password-input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={isRTL ? 'أدخل كلمة المرور' : 'Enter password'}
                  className={`w-full h-10 rounded-lg border border-slate-200 bg-slate-50 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition-colors ${isRTL ? 'pr-3 pl-10 text-right' : 'pl-3 pr-10'}`}
                  autoComplete="current-password"
                />
                <button type="button" onClick={() => setShowPassword(!showPassword)}
                  className="absolute top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 z-10"
                  style={{ [isRTL ? 'left' : 'right']: '0.75rem' }}>
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <Button
              type="submit"
              data-testid="login-submit-btn"
              disabled={loading}
              className="w-full h-10 text-sm font-medium"
              style={{ backgroundColor: 'var(--theme-primary, #0F172A)', color: 'var(--theme-primary-foreground, #fff)' }}
            >
              {loading ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
              ) : (
                <><LogIn className={`w-4 h-4 ${isRTL ? 'ml-2' : 'mr-2'}`} />{isRTL ? 'تسجيل الدخول' : 'Sign In'}</>
              )}
            </Button>
          </form>
        </div>

        <p className="text-center text-xs text-slate-400 mt-6">AZIZI.CO v1.0</p>
      </div>
    </div>
  );
};
